import '@/global.css';

import * as React from 'react';
import { AccountContextSheet } from '@/features/workspaces/AccountContextSheet';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { Sentry, addSentryActionBreadcrumb, setSentryScreen } from '@/lib/analytics/sentry';
import { GoogleMapsProvider } from '@/features/map/GoogleMapsProvider';
import { ToastViewport } from '@/components/ui/ToastViewport';
import { AuthProvider, useAuth } from '@/lib/auth/auth';
import { getEventHref, getRouteEntityID, getVenueHref } from '@/lib/navigation/routes';
import {
  addNotificationResponseReceivedListener,
  addPushTokenListener,
  getNotificationPermissionStatus,
  getCurrentExpoPushToken,
  handleNotificationResponse,
  setupAndroidChannel,
} from '@/lib/notifications/notifications';
import { registerPushToken } from '@/lib/notifications/registerPushToken';
import {
  getAppearanceColorScheme,
  useAppearance,
  useAppearanceScheduler,
} from '@/lib/design/appearance';
import { useOnboarding } from '@/features/onboarding/onboarding';
import {
  flushPostHog,
  flushPostHogInBackground,
  initPostHog,
  trackAppOpen,
  trackScreenView,
  trackWebPageView,
} from '@/lib/analytics/posthog';
import { useAppVersion } from '@/lib/useAppVersion';
import { useNotificationPreferences } from '@/features/account/useNotificationPreferences';
import { syncCurrentPushToken } from '@/features/account/pushPreferenceFlow';
import { DESIGN_TOKENS, NAV_THEME } from '@/lib/design/theme';
import { APP_FONTS } from '@/lib/design/typography';
import { StatusNoticeBanner } from '@/features/status/StatusNoticeBanner';
import { LocationProvider, locationStore } from '@/lib/location/location';
import { getActivityPocketsFromResponse } from '@/features/map/useActivityPockets';
import { getActivityPocketsQueryOptions } from '@/features/map/mapQuery';
import { DISCOVER_RADIUS_METERS, type FeedMode } from '@/features/events/feed/feedQuery';
import { useFeedPreferences } from '@/features/events/feed/feedPreferences';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import { getNearbyFeedQueryOptions } from '@/features/events/feed/useNearbyFeed';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { ThemeProvider } from '@react-navigation/native';
import { PortalHost } from '@rn-primitives/portal';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useFonts } from 'expo-font';
import * as Linking from 'expo-linking';
import * as Notifications from 'expo-notifications';
import { Redirect, Stack, router, usePathname, useSegments } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import * as SystemUI from 'expo-system-ui';
import { useColorScheme } from 'nativewind';
import { AppState, Keyboard, Platform, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { configureGoogleSignIn } from '@/lib/auth/googleAuth';
import { shouldClearAccountQueries } from '@/lib/auth/accountQueryCache';
import {
  scheduleTimeout,
  subscribeToAppStateChange,
} from '@/lib/app/nativeEventSubscriptions';
import type { MarketActivityPocketListResponse } from '@/lib/api/apiTypes';
import { canHandleColdLaunchRoute } from '@/lib/navigation/coldLaunchRouting';
import { getEventDeepLinkAttribution } from '@/lib/navigation/eventDeepLinkAttribution';

export {
  // Catch any errors thrown by the Layout component.
  ErrorBoundary,
} from 'expo-router';

SplashScreen.preventAutoHideAsync().catch(() => undefined);

const PUSH_TOKEN_RETRY_MS = 30_000;

function navigateToDeepLink(url: string) {
  addSentryActionBreadcrumb({
    screen: 'root',
    action: 'open_deep_link',
    data: { url },
  });
  const { path, queryParams } = Linking.parse(url);
  if (!path) return;

  const normalizedPath = path.replace(/^\/+/, '');
  const token =
    typeof queryParams?.token === 'string'
      ? queryParams.token
      : Array.isArray(queryParams?.token)
        ? queryParams.token[0]
        : undefined;

  const eventMatch = normalizedPath.match(/^events?\/([^/]+)$/);
  if (eventMatch) {
    const eventID = getRouteEntityID(eventMatch[1]);
    if (eventID) {
      router.navigate(
        getEventHref(eventID, undefined, getEventDeepLinkAttribution(queryParams)) as never
      );
    }
    return;
  }

  const venueApprovedMatch = normalizedPath.match(/^venues\/([^/]+)\/approved$/);
  if (venueApprovedMatch) {
    const venueID = getRouteEntityID(venueApprovedMatch[1]);
    if (venueID) router.navigate(`/venues/${venueID}/approved` as never);
    return;
  }

  const venueMatch = normalizedPath.match(/^venues\/([^/]+)$/);
  if (venueMatch) {
    const venueID = getRouteEntityID(venueMatch[1]);
    if (venueID) router.navigate(getVenueHref(venueID) as never);
    return;
  }

  const profileMatch = normalizedPath.match(/^profiles\/([^/]+)$/);
  if (profileMatch) {
    router.navigate(`/profiles/${profileMatch[1]}` as never);
    return;
  }

  const invitationMatch = normalizedPath.match(/^invitations\/([^/]+)$/);
  if (invitationMatch) {
    router.navigate(`/invitations/${invitationMatch[1]}` as never);
    return;
  }

  if (normalizedPath === 'verify-email' && token) {
    router.navigate({ pathname: '/verify-email', params: { token } } as never);
    return;
  }

  if (normalizedPath === 'reset-password' && token) {
    router.navigate({ pathname: '/reset-password', params: { token } } as never);
  }
}

function RootLayout() {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            staleTime: 30000,
          },
        },
      })
  );
  const { colorScheme, setColorScheme } = useColorScheme();
  const appearanceHydrated = useAppearance((state) => state.isHydrated);
  const resolvedMode = useAppearance((state) => state.resolvedMode);
  const hydrateAppearance = useAppearance((state) => state.hydrate);
  const appColorScheme = getAppearanceColorScheme(resolvedMode);
  const session = useAuth((state) => state.session);
  const authIsHydrating = useAuth((state) => state.isHydrating);
  const request = useAuth((state) => state.request);
  const userOrigin = useUserOriginCoords();
  const feedTab = useFeedPreferences((state) => state.feedTab);
  const timeWindow = useFeedPreferences((state) => state.timeWindow);
  const radiusMeters = useFeedPreferences((state) => state.radiusMeters);
  const locationCoords = locationStore((state) => state.coords);
  const locationIsReady = locationStore((state) => state.isReady);
  const locationPermissionDenied = locationStore((state) => state.permissionDenied);
  const segments = [...useSegments()];
  const rootSegment = segments[0];
  const isLightweightPublicWebPage =
    Platform.OS === 'web' &&
    (rootSegment === 'about' ||
      rootSegment === 'download' ||
      rootSegment === 'privacy' ||
      rootSegment === 'privacy-policy' ||
      rootSegment === 'support' ||
      rootSegment === 'terms' ||
      rootSegment === 'terms-of-service');
  const [splashWaitTimedOut, setSplashWaitTimedOut] = React.useState(false);
  const splashHiddenRef = React.useRef(false);
  const startupPrefetchKeyRef = React.useRef<string | null>(null);
  const previousUserIDRef = React.useRef(session?.me.user.id ?? null);

  useAppearanceScheduler(appearanceHydrated);

  React.useEffect(() => {
    configureGoogleSignIn();
  }, []);

  React.useEffect(() => {
    initPostHog();
  }, []);

  React.useEffect(() => {
    const userID = session?.me.user.id ?? null;
    if (shouldClearAccountQueries(previousUserIDRef.current, userID)) {
      queryClient.clear();
    }
    previousUserIDRef.current = userID;
  }, [queryClient, session?.me.user.id]);

  React.useEffect(() => {
    hydrateAppearance().catch(() => undefined);
  }, [hydrateAppearance]);

  const [fontsLoaded] = useFonts(APP_FONTS);
  const appReadyForSplashDismiss = fontsLoaded && appearanceHydrated && !authIsHydrating;

  React.useEffect(() => {
    if (!appearanceHydrated) {
      return;
    }

    if (colorScheme !== appColorScheme) {
      setColorScheme(appColorScheme);
    }
  }, [appColorScheme, appearanceHydrated, colorScheme, setColorScheme]);

  React.useEffect(() => {
    if (!appearanceHydrated) {
      return;
    }

    SystemUI.setBackgroundColorAsync(DESIGN_TOKENS.colors.canvas).catch(() => undefined);
  }, [appearanceHydrated, resolvedMode]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setSplashWaitTimedOut(true);
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  React.useEffect(() => {
    if (splashHiddenRef.current) {
      return;
    }

    if (appReadyForSplashDismiss || splashWaitTimedOut) {
      splashHiddenRef.current = true;
      SplashScreen.hideAsync().catch(() => undefined);
    }
  }, [appReadyForSplashDismiss, splashWaitTimedOut]);

  React.useEffect(() => {
    if (!appReadyForSplashDismiss || isLightweightPublicWebPage) {
      return;
    }

    const feedMode: FeedMode = feedTab === 'discover' ? 'smart' : 'proximity';
    const feedRadiusMeters = feedTab === 'discover' ? DISCOVER_RADIUS_METERS : radiusMeters;
    const feedQueryParams = {
      attributes: [],
      entryType: null,
      hasSession: Boolean(session),
      mode: feedMode,
      originLatitude: userOrigin.latitude,
      originLongitude: userOrigin.longitude,
      originSource: userOrigin.source,
      radiusMeters: feedRadiusMeters,
      timeWindow,
      viewerKey: session?.me.user.id ?? 'anon',
    };
    const feedQueryOptions = getNearbyFeedQueryOptions(feedQueryParams, request);
    const activityPocketQueryOptions =
      locationIsReady && !locationPermissionDenied
        ? getActivityPocketsQueryOptions(
            {
              latitude: locationCoords.latitude,
              longitude: locationCoords.longitude,
            },
            (path) => request<MarketActivityPocketListResponse>(path),
            getActivityPocketsFromResponse
          )
        : null;
    const prefetchKey = JSON.stringify([
      feedQueryOptions.queryKey,
      activityPocketQueryOptions?.queryKey ?? null,
    ]);
    if (startupPrefetchKeyRef.current === prefetchKey) {
      return;
    }

    startupPrefetchKeyRef.current = prefetchKey;
    queryClient
      .prefetchInfiniteQuery({
        ...feedQueryOptions,
      })
      .catch(() => undefined);

    if (!activityPocketQueryOptions) {
      return;
    }

    queryClient
      .prefetchQuery({
        ...activityPocketQueryOptions,
      })
      .catch(() => undefined);
  }, [
    appReadyForSplashDismiss,
    feedTab,
    isLightweightPublicWebPage,
    locationCoords.latitude,
    locationCoords.longitude,
    locationIsReady,
    locationPermissionDenied,
    queryClient,
    radiusMeters,
    request,
    session,
    timeWindow,
    userOrigin.latitude,
    userOrigin.longitude,
    userOrigin.source,
  ]);

  const appProviders = (
    <AuthProvider>
      <LocationProvider>
        <StatusBar
          style={appColorScheme === 'light' ? 'dark' : 'light'}
          backgroundColor={DESIGN_TOKENS.colors.canvas}
        />
        <StatusNoticeBanner enabled={!isLightweightPublicWebPage} />
        <RootNavigator readyForColdLaunchNotifications={appReadyForSplashDismiss} />
        <AccountContextSheet />
        <ToastViewport />
        <PortalHost name="tab-bar" />
        <PortalHost />
      </LocationProvider>
    </AuthProvider>
  );
  const appShell = isLightweightPublicWebPage ? (
    appProviders
  ) : (
    <GoogleMapsProvider>{appProviders}</GoogleMapsProvider>
  );

  if ((!fontsLoaded || !appearanceHydrated) && !splashWaitTimedOut) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }}>
      <BottomSheetModalProvider>
        <SafeAreaProvider style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider value={NAV_THEME[appColorScheme]}>
              <View style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }}>
                {appShell}
              </View>
            </ThemeProvider>
          </QueryClientProvider>
        </SafeAreaProvider>
      </BottomSheetModalProvider>
    </GestureHandlerRootView>
  );
}

export default Sentry.wrap(RootLayout);

function getNotificationResponseID(response: unknown) {
  return response &&
    typeof response === 'object' &&
    'notification' in response &&
    response.notification &&
    typeof response.notification === 'object' &&
    'request' in response.notification &&
    response.notification.request &&
    typeof response.notification.request === 'object' &&
    'identifier' in response.notification.request &&
    typeof response.notification.request.identifier === 'string'
    ? response.notification.request.identifier
    : null;
}

function RootNavigator({
  readyForColdLaunchNotifications,
}: {
  readyForColdLaunchNotifications: boolean;
}) {
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const { currentContext, isHydrating: isAccountContextHydrating } = useAccountContext();
  const setAppearanceRouteOverride = useAppearance((state) => state.setRouteOverride);

  // null = no deep link, undefined = still loading
  const [initialURL, setInitialURL] = React.useState<string | null | undefined>(undefined);
  React.useEffect(() => {
    Linking.getInitialURL().then((url) => setInitialURL(url ?? null));
  }, []);
  const notificationPreferencesQuery = useNotificationPreferences();
  const userID = session?.me.user.id ?? null;
  const accessToken = session?.accessToken ?? null;
  const isAuthenticated = Boolean(session);
  const userCounty = session?.me.profile.last_county_name ?? null;
  const isVenueAccount = session?.me.profile.is_venue ?? false;
  const pushEnabled = notificationPreferencesQuery.data?.preferences.push_enabled === true;
  const pathname = usePathname();
  const segments = [...useSegments()];
  const rootSegment = segments[0];
  const tabSegment = segments[1];
  const isNightOnlyWebPage =
    Platform.OS === 'web' && ((!rootSegment && pathname === '/') || rootSegment === 'download');
  const isDayOnlyAuthRoute =
    rootSegment === 'sign-in' ||
    rootSegment === 'auth' ||
    rootSegment === 'verify-email' ||
    rootSegment === 'reset-password';
  const isPublicWebEventsTab =
    Platform.OS === 'web' && rootSegment === '(tabs)' && tabSegment === 'events';
  const isPublicRoute =
    !rootSegment ||
    (rootSegment === '(tabs)' && tabSegment !== 'profile') ||
    rootSegment === 'event' ||
    rootSegment === 'events' ||
    rootSegment === 'map-preview' ||
    rootSegment === 'profiles' ||
    rootSegment === 'venues' ||
    rootSegment === 'search' ||
    rootSegment === 'activity' ||
    rootSegment === 'update-required' ||
    rootSegment === 'sign-in' ||
    rootSegment === 'auth' ||
    rootSegment === 'verify-email' ||
    rootSegment === 'reset-password' ||
    rootSegment === 'about' ||
    rootSegment === 'download' ||
    rootSegment === 'privacy' ||
    rootSegment === 'privacy-policy' ||
    rootSegment === 'support' ||
    rootSegment === 'terms' ||
    rootSegment === 'terms-of-service';

  const onboardingComplete = useOnboarding((state) => state.isComplete);
  const profileSetupPendingUserID = useOnboarding((state) => state.profileSetupPendingUserID);
  const hydrateOnboarding = useOnboarding((state) => state.hydrate);
  const versionQuery = useAppVersion(!isHydrating);
  const versionGateMessage = versionQuery.data?.update_message?.trim() || undefined;
  const updateRequired = versionQuery.data?.update_required === true;
  const versionResolved = versionQuery.isSuccess || versionQuery.isError;

  React.useLayoutEffect(() => {
    setAppearanceRouteOverride(isNightOnlyWebPage ? 'night' : isDayOnlyAuthRoute ? 'day' : null);

    return () => {
      setAppearanceRouteOverride(null);
    };
  }, [isDayOnlyAuthRoute, isNightOnlyWebPage, setAppearanceRouteOverride]);

  // Set up Android notification channel once on mount
  React.useEffect(() => {
    setupAndroidChannel();
  }, []);

  const appStateRef = React.useRef(AppState.currentState);
  const lastAppOpenAtRef = React.useRef(0);

  const trackAppOpenIfNeeded = React.useCallback(() => {
    if (isHydrating) {
      return;
    }

    const now = Date.now();
    if (lastAppOpenAtRef.current !== 0 && now - lastAppOpenAtRef.current < 5 * 60 * 1000) {
      return;
    }

    lastAppOpenAtRef.current = now;
    trackAppOpen({
      isAuthenticated,
      route: pathname,
      userCounty,
    });
  }, [isAuthenticated, isHydrating, pathname, userCounty]);

  React.useEffect(() => {
    trackAppOpenIfNeeded();
  }, [trackAppOpenIfNeeded]);

  React.useEffect(() => {
    if (!pathname) {
      return;
    }

    if (Platform.OS === 'web') {
      trackWebPageView(pathname);
      return;
    }

    trackScreenView({
      isAuthenticated,
      pathname,
      rootSegment: typeof rootSegment === 'string' ? rootSegment : null,
      tabSegment: typeof tabSegment === 'string' ? tabSegment : null,
    });
  }, [isAuthenticated, pathname, rootSegment, tabSegment]);

  React.useEffect(() => {
    if (!pathname) {
      return;
    }

    setSentryScreen(pathname, {
      root_segment: typeof rootSegment === 'string' ? rootSegment : null,
      tab_segment: typeof tabSegment === 'string' ? tabSegment : null,
      is_authenticated: isAuthenticated,
      is_venue_account: isVenueAccount,
    });
  }, [isAuthenticated, isVenueAccount, pathname, rootSegment, tabSegment]);

  React.useEffect(() => {
    return subscribeToAppStateChange((nextState) => {
      const previousState = appStateRef.current;
      appStateRef.current = nextState;
      if (
        nextState === 'active' &&
        (previousState === 'background' || previousState === 'inactive')
      ) {
        Keyboard.dismiss();
        trackAppOpenIfNeeded();
        void flushPostHog();
        return;
      }
      if (nextState === 'background' || nextState === 'inactive') {
        void flushPostHogInBackground();
      }
    });
  }, [trackAppOpenIfNeeded]);

  // Read onboarding flag (uses localStorage on web, SecureStore on native)
  React.useEffect(() => {
    hydrateOnboarding();
  }, [hydrateOnboarding]);

  const registeredPushTokenKeyRef = React.useRef<string | null>(null);

  // Push registration intentionally bypasses session refresh so a rejected token cannot log the user out.
  React.useEffect(() => {
    if (!userID || !accessToken) {
      registeredPushTokenKeyRef.current = null;
      return;
    }
    if (notificationPreferencesQuery.isPending) {
      return;
    }
    if (notificationPreferencesQuery.isError) {
      return;
    }
    if (!pushEnabled) {
      return;
    }
    let cancelled = false;
    let syncing = false;
    let cancelRetry: (() => void) | null = null;

    const scheduleRetry = () => {
      if (cancelled || cancelRetry) {
        return;
      }
      cancelRetry = scheduleTimeout(() => {
        cancelRetry = null;
        void syncToken();
      }, PUSH_TOKEN_RETRY_MS);
    };

    const syncToken = async () => {
      if (syncing) {
        scheduleRetry();
        return;
      }
      syncing = true;
      const result = await syncCurrentPushToken({
        userID,
        registeredKey: registeredPushTokenKeyRef.current,
        getPermissionStatus: getNotificationPermissionStatus,
        getToken: getCurrentExpoPushToken,
        registerToken: (token) => registerPushToken(token, accessToken),
      });
      syncing = false;
      if (cancelled) {
        return;
      }
      if (result.registeredKey) {
        registeredPushTokenKeyRef.current = result.registeredKey;
        cancelRetry?.();
        cancelRetry = null;
      }
      if (result.retry) {
        scheduleRetry();
      }
    };

    const tokenSubscription = addPushTokenListener(() => {
      void syncToken();
    });
    void syncToken();

    return () => {
      cancelled = true;
      tokenSubscription.remove();
      cancelRetry?.();
    };
  }, [
    accessToken,
    notificationPreferencesQuery.isError,
    notificationPreferencesQuery.isPending,
    pushEnabled,
    userID,
  ]);

  const handledColdLaunchNotificationRef = React.useRef(false);

  // Navigate to event when user taps a notification
  React.useEffect(() => {
    const sub = addNotificationResponseReceivedListener((response) => {
      const notificationID = getNotificationResponseID(response);
      addSentryActionBreadcrumb({
        screen: 'root',
        action: 'open_notification',
        data: {
          notification_id: notificationID,
        },
      });
      handleNotificationResponse(response);
    });
    return () => sub.remove();
  }, []);

  React.useEffect(() => {
    if (!canHandleColdLaunchRoute({
      handled: handledColdLaunchNotificationRef.current,
      sourceReady: readyForColdLaunchNotifications,
      authHydrating: isHydrating,
      onboardingComplete,
    })) {
      return;
    }

    handledColdLaunchNotificationRef.current = true;
    let cancelled = false;

    Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        if (cancelled || !response) {
          return;
        }

        addSentryActionBreadcrumb({
          screen: 'root',
          action: 'open_cold_launch_notification',
          data: {
            notification_id: getNotificationResponseID(response),
          },
        });
        handleNotificationResponse(response);
        return Notifications.clearLastNotificationResponseAsync();
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [isHydrating, onboardingComplete, readyForColdLaunchNotifications]);

  // Handle cold-launch deep links. initialURL is undefined while the promise
  // is in flight, null if there was none, or a string if there is one.
  // We wait until both the URL is resolved and hydration is done before navigating.
  const handledInitialURLRef = React.useRef(false);
  React.useEffect(() => {
    if (!canHandleColdLaunchRoute({
      handled: handledInitialURLRef.current,
      sourceReady: initialURL !== undefined,
      authHydrating: isHydrating,
      onboardingComplete,
    })) {
      return;
    }
    handledInitialURLRef.current = true;
    if (initialURL) navigateToDeepLink(initialURL);
  }, [initialURL, isHydrating, onboardingComplete]);

  // Block render until auth, onboarding, account context, and the version gate are resolved.
  if (isHydrating || onboardingComplete === null || isAccountContextHydrating || !versionResolved) {
    return <View style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }} />;
  }

  if (updateRequired && rootSegment !== 'update-required') {
    return (
      <Redirect
        href={{
          pathname: '/update-required',
          params: versionGateMessage ? { message: versionGateMessage } : {},
        }}
      />
    );
  }

  if (!updateRequired && rootSegment === 'update-required') {
    return <Redirect href="/(tabs)/events" />;
  }

  if (!session && !isPublicRoute) {
    return <Redirect href="/(tabs)/events" />;
  }

  // Fresh users who haven't completed onboarding should see sign-in first
  if (!session && !onboardingComplete && rootSegment === '(tabs)' && !isPublicWebEventsTab) {
    return <Redirect href="/sign-in" />;
  }

  // Authenticated but hasn't completed onboarding.
  // A user with an existing profile (username set) is treated as done regardless
  // of the local SecureStore flag — handles the case where they log in on a new device.
  const hasProfile = Boolean(session?.me?.profile?.username);
  const needsProfileSetup = profileSetupPendingUserID === session?.me.user.id;
  if (session && needsProfileSetup && rootSegment !== 'onboarding') {
    return <Redirect href="/onboarding/profile-setup" />;
  }
  if (session && !onboardingComplete && !hasProfile && rootSegment !== 'onboarding') {
    return <Redirect href="/onboarding" />;
  }

  if (session && currentContext?.kind === 'business') {
    if (rootSegment === '(tabs)') {
      return <Redirect href={'/(business)/home' as never} />;
    }
  } else if (rootSegment === '(business)') {
    return <Redirect href="/(tabs)/events" />;
  }

  return (
    <Stack>
      <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
      <Stack.Screen name="(business)" options={{ headerShown: false }} />
      <Stack.Screen name="onboarding" options={{ headerShown: false, animation: 'fade' }} />
      <Stack.Screen
        name="events/create"
        options={{
          headerShown: false,
          presentation: 'modal',
          animation: 'slide_from_bottom',
        }}
      />
      <Stack.Screen
        name="events/[id]/edit"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="sign-in"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="auth"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="map-preview/[id]"
        options={{
          headerShown: false,
          presentation: 'transparentModal',
          animation: 'slide_from_bottom',
        }}
      />
      <Stack.Screen
        name="event/[id]"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="events/[id]/index"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="venues/[id]/index"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="venues/[id]/claim"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="venues/[id]/edit"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="venues/[id]/approved"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="profiles/[username]"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="search"
        options={{
          headerShown: false,
          animation: 'fade_from_bottom',
          animationDuration: 180,
        }}
      />
      <Stack.Screen
        name="notifications"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="workspaces/index"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="workspaces/[id]"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="workspaces/[id]/events"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="workspaces/[id]/team"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="workspaces/[id]/organization"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="invitations/[token]"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="settings"
        options={{
          headerShown: false,
          gestureEnabled: true,
        }}
      />
      <Stack.Screen
        name="settings-notifications"
        options={{
          headerShown: false,
          gestureEnabled: true,
        }}
      />
      <Stack.Screen
        name="settings-privacy"
        options={{
          headerShown: false,
          gestureEnabled: true,
        }}
      />
      <Stack.Screen
        name="privacy-policy"
        options={{
          headerShown: false,
          gestureEnabled: true,
        }}
      />
      <Stack.Screen
        name="followers"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="verify-email"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="reset-password"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="update-required"
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />
    </Stack>
  );
}
